import React, { useState } from 'react'
import { Form, Button, Row, Col, InputGroup, Spinner } from 'react-bootstrap'
import useCreateMacMini, {
  CreateMacMiniParams,
} from 'hooks/use-create-mac-mini'
import { useNextDeviceNames } from 'hooks/use-next-device-names'
import toast from 'react-hot-toast'

interface AddMacMiniFormProps {
  terminalId: string
  terminalName: string
  onSuccess?: () => void
}

export const AddMacMiniForm: React.FC<AddMacMiniFormProps> = ({
  terminalId,
  terminalName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    model: 'M2',
    serialNumber: '',
    macAddress: '',
    ipAddress: '',
    count: 1,
  })

  const createMacMini = useCreateMacMini()

  const { data: namesData, isLoading: isLoadingNames } = useNextDeviceNames({
    terminalId,
    terminalName,
    deviceType: 'mac-mini',
    count: formData.count,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!namesData?.names) {
      toast.error('Device names not generated yet')
      return
    }

    if (!formData.serialNumber || !formData.macAddress) {
      toast.error('Please fill in all required fields')
      return
    }

    // Validate MAC address format
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/
    if (!macRegex.test(formData.macAddress)) {
      toast.error(
        'MAC address must be in format XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX with hex digits (0-9, A-F)'
      )
      return
    }

    try {
      const createParams: CreateMacMiniParams = {
        terminalId,
        model: formData.model,
        serialNumber: formData.serialNumber,
        macAddress: formData.macAddress,
        ipAddress: formData.ipAddress || undefined,
        names: namesData.names,
      }

      await createMacMini.mutateAsync(createParams)

      // Reset form
      setFormData({
        model: 'M2',
        serialNumber: '',
        macAddress: '',
        ipAddress: '',
        count: 1,
      })

      onSuccess?.()
    } catch (error) {
      // Error handling is done in the hook
    }
  }

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Model *</Form.Label>
            <Form.Control
              as="select"
              value={formData.model}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleInputChange('model', e.target.value)
              }
              required
            >
              <option value="M1">M1</option>
              <option value="M2">M2</option>
              <option value="M3">M3</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Count</Form.Label>
            <Form.Control
              type="number"
              value={1}
              disabled
              readOnly
              className="bg-secondary text-white"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Serial Number *</Form.Label>
            <Form.Control
              type="text"
              value={formData.serialNumber}
              onChange={(e) =>
                handleInputChange('serialNumber', e.target.value)
              }
              placeholder="MM-XXXX-2024"
              required
            />
            <Form.Text className="text-muted">
              Serial number for the Mac Mini
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>MAC Address *</Form.Label>
            <Form.Control
              type="text"
              value={formData.macAddress}
              onChange={(e) => handleInputChange('macAddress', e.target.value)}
              placeholder="00:1A:2B:3C:4D:5E"
              pattern="^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$"
              required
            />
            <Form.Text className="text-muted">
              MAC address in format XX:XX:XX:XX:XX:XX (e.g., 00:1A:2B:3C:4D:5E)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>IP Address (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={formData.ipAddress}
              onChange={(e) => handleInputChange('ipAddress', e.target.value)}
              placeholder="192.168.1.XXX"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Generated Names Preview</Form.Label>
            <InputGroup>
              <Form.Control
                value={
                  isLoadingNames
                    ? 'Loading...'
                    : namesData?.names.join(', ') || ''
                }
                readOnly
                className="bg-light"
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Names will be auto-generated based on current received devices
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button
          type="submit"
          variant="success"
          disabled={createMacMini.isLoading || isLoadingNames}
        >
          {createMacMini.isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            `Create Mac Mini`
          )}
        </Button>
      </div>
    </Form>
  )
}
